import React, { useRef, useEffect, useCallback, useId } from 'react';
import OverType, { type OverTypeInstance } from 'overtype';
import { cn } from '@/lib/utils';

interface EditorOvertypeProps {
  placeholder?: string;
  value?: string; // Controlled value
  initialContent?: string; // For backward compatibility
  contentType?: 'markdown' | 'text'; // Simplified from lexical - overtype works with markdown
  onChange?: (content: string) => void;
  onBlur?: () => void;
  className?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  showToolbar?: boolean;
  fontFamily?: string; // Custom font family
  fontSize?: string; // Custom font size
  lineHeight?: string | number; // Custom line height
}

// Generate CSS overrides for the editor instance
// OverType injects global styles with !important, so we need higher specificity
// Exported for the alignment test — the caret's position depends on what this emits.
export function generateFontOverrideStyles(
  instanceId: string,
  fontFamily: string,
  fontSize?: string,
  lineHeight?: string | number,
  readOnly?: boolean
): string {
  const fontSizeRule = fontSize ? `font-size: ${fontSize} !important;` : '';
  const lineHeightRule = lineHeight ? `line-height: ${lineHeight} !important;` : '';

  // In read-only mode, OverType's autoResize is disabled so the container defaults to
  // height:100% (which collapses when the parent has no explicit height). Override to
  // let the preview flow naturally so the outer overflow-y-auto container can scroll it.
  const readOnlyHeightStyles = readOnly ? `
    #${instanceId}.overtype-editor .overtype-container {
      height: auto !important;
    }
    #${instanceId}.overtype-editor .overtype-wrapper {
      flex: none !important;
      height: auto !important;
      overflow: visible !important;
    }
    #${instanceId}.overtype-editor .overtype-preview {
      position: relative !important;
      height: auto !important;
      overflow: visible !important;
    }
  ` : '';

  return `
    #${instanceId}.overtype-editor .overtype-container,
    #${instanceId}.overtype-editor .overtype-container *,
    #${instanceId}.overtype-editor .overtype-wrapper .overtype-input,
    #${instanceId}.overtype-editor .overtype-wrapper .overtype-preview,
    #${instanceId}.overtype-editor .overtype-container[data-mode="preview"] .overtype-wrapper .overtype-preview {
      font-family: ${fontFamily} !important;
      ${fontSizeRule}
      ${lineHeightRule}
    }
    /*
     * Code elements are monospace ONLY when there is no textarea to stay aligned with.
     *
     * OverType draws a transparent textarea exactly on top of the rendered preview, so the caret
     * lands where the glyph is. That only holds while both layers use identical metrics — the
     * library is explicit about it ("Match textarea font exactly for alignment") and deliberately
     * gives preview code elements the SAME font as the textarea.
     *
     * Forcing monospace here broke that: the textarea has no code element, so an inline code span
     * rendered ~32px wider in the preview than in the textarea behind it. Everything after it on
     * that line was offset, the caret sat two or three characters from the character it pointed
     * at, and a line long enough to wrap wrapped in one layer before the other — which is how the
     * caret ends up on a different line entirely.
     *
     * In preview mode the textarea is display:none, so there is nothing to align with and
     * monospace is free.
     */
    #${instanceId}.overtype-editor .overtype-container[data-mode="preview"] .overtype-wrapper code,
    #${instanceId}.overtype-editor .overtype-container[data-mode="preview"] .overtype-wrapper pre,
    #${instanceId}.overtype-editor .overtype-container[data-mode="preview"] .overtype-wrapper .code-block {
      font-family: "SF Mono", SFMono-Regular, Menlo, Monaco, Consolas, monospace !important;
    }
    ${readOnlyHeightStyles}
  `;
}

export const Editor = React.forwardRef<HTMLDivElement, EditorOvertypeProps>(
  (
    {
      placeholder = 'Enter some text...',
      value,
      initialContent,
      onChange,
      className,
      autoFocus = false,
      readOnly = false,
      showToolbar = true,
      fontFamily = 'Inter, sans-serif',
      fontSize = '14px',
      lineHeight = '1.5'
    },
    ref
  ) => {
    // Use value if provided (controlled mode), otherwise use initialContent
    const content = value !== undefined ? value : initialContent || '';
    const editorRef = useRef<HTMLDivElement>(null);
    const overtypeInstanceRef = useRef<OverTypeInstance | null>(null);
    const styleRef = useRef<HTMLStyleElement | null>(null);

    // Generate a unique, CSS-safe ID for this instance
    const reactId = useId();
    const instanceId = `overtype-${reactId.replace(/:/g, '-')}`;

    // Handle content changes
    const handleChange = useCallback(
      (value: string) => {
        if (onChange) {
          onChange(value);
        }
      },
      [onChange]
    );

    // Inject custom font styles to override OverType's global !important styles
    useEffect(() => {
      // Remove existing style if any
      if (styleRef.current) {
        styleRef.current.remove();
        styleRef.current = null;
      }

      // Create and inject new style element
      const styleEl = document.createElement('style');
      styleEl.className = `overtype-custom-${instanceId}`;
      styleEl.textContent = generateFontOverrideStyles(instanceId, fontFamily, fontSize, lineHeight, readOnly);
      document.head.appendChild(styleEl);
      styleRef.current = styleEl;

      return () => {
        if (styleRef.current) {
          styleRef.current.remove();
          styleRef.current = null;
        }
      };
    }, [instanceId, fontFamily, fontSize, lineHeight, readOnly]);

    // Initialize/Reinitialize OverType editor when readOnly or showToolbar changes
    useEffect(() => {
      if (!editorRef.current) {
        console.log('No editor ref available');
        return;
      }

      // Destroy existing instance if it exists
      if (overtypeInstanceRef.current) {
        console.log('Destroying existing OverType instance for reinitialization');
        overtypeInstanceRef.current.destroy?.();
        overtypeInstanceRef.current = null;
      }

      console.log('Initializing/Reinitializing OverType...', {
        element: editorRef.current,
        hasInstance: !!overtypeInstanceRef.current,
        readOnly,
        showToolbar
      });

      try {
        // Use OverType.init as shown in the reference function
        // Note: fontFamily option doesn't work due to library bug - we inject custom CSS instead
        const [instance] = OverType.init(editorRef.current, {
          value: content || '',
          placeholder: placeholder || 'Enter some text...',
          toolbar: showToolbar && !readOnly,
          autofocus: autoFocus,
          onChange: handleChange,
          autoResize: !readOnly
        });

        overtypeInstanceRef.current = instance;

        // If readOnly, enable preview mode to prevent editing
        if (readOnly && instance.showPreviewMode) {
          instance.showPreviewMode();
          console.log('Preview mode enabled for readonly editor');
        }

        console.log('OverType initialized successfully', {
          instance,
          element: editorRef.current,
          children: editorRef.current.children.length,
          readOnly,
          previewMode: readOnly
        });
      } catch (error) {
        console.error('Failed to initialize OverType:', error);
        console.error('OverType available:', typeof OverType);
        console.error('OverType methods:', Object.getOwnPropertyNames(OverType));
      }

      // Cleanup
      return () => {
        if (overtypeInstanceRef.current) {
          console.log('Destroying OverType instance');
          overtypeInstanceRef.current.destroy?.();
          overtypeInstanceRef.current = null;
        }
      };
    }, [readOnly, showToolbar]); // Re-run when editor config changes

    // Update value when content changes externally (controlled mode)
    useEffect(() => {
      if (overtypeInstanceRef.current && content !== overtypeInstanceRef.current.getValue()) {
        overtypeInstanceRef.current.setValue(content);
      }
    }, [content]);

    // Forward ref
    React.useImperativeHandle(ref, () => editorRef.current!, []);

    return <div ref={editorRef} id={instanceId} className={cn('overtype-editor', className)} />;
  }
);

Editor.displayName = 'Editor';
